import { createContext, useContext, useLayoutEffect, useMemo, useState, ReactNode } from 'react';

// project-imports
import { ThemeMode } from 'config';

// ==============================|| CONTEXT - THEME SCOPE ||============================== //

/**
 * Permite que una rama del árbol (login, activación, flujo del tercero) fije el modo de
 * color sin importar la preferencia guardada del usuario.
 *
 * Vive por encima del proveedor de tema a propósito: así existe un único proveedor en toda
 * la app y un único punto que escribe el atributo `data-clac-mode`. Anidar un segundo
 * proveedor duplicaría la construcción del tema y dejaría las variables CSS peleadas entre sí.
 */

type ThemeScopeValue = {
  forcedMode: ThemeMode | null;
  setForcedMode: (mode: ThemeMode | null) => void;
};

const ThemeScopeContext = createContext<ThemeScopeValue>({ forcedMode: null, setForcedMode: () => {} });

export function ThemeScopeProvider({ children }: { children: ReactNode }) {
  const [forcedMode, setForcedMode] = useState<ThemeMode | null>(null);
  const value = useMemo(() => ({ forcedMode, setForcedMode }), [forcedMode]);

  return <ThemeScopeContext.Provider value={value}>{children}</ThemeScopeContext.Provider>;
}

export function useThemeScope() {
  return useContext(ThemeScopeContext);
}

/**
 * Componente sin salida visual. Mientras esté montado, fija el modo indicado; al
 * desmontarse devuelve el control a la preferencia del usuario.
 */
export function ForceThemeMode({ mode }: { mode: ThemeMode }) {
  const { setForcedMode } = useThemeScope();

  // useLayoutEffect y no useEffect: se aplica antes del primer pintado, así el usuario
  // que dejó el sistema en oscuro no ve un parpadeo al llegar al login.
  useLayoutEffect(() => {
    setForcedMode(mode);
    return () => setForcedMode(null);
  }, [mode, setForcedMode]);

  return null;
}
